import React, { useEffect, useId, useRef, useState } from 'react';
import { type NavItemNoIcon } from '@cx-web/domain-types';
import classnames from 'classnames';

import Icon from '../../../../../primitives/icon/index';
import caretStyles from '../../dropdownCaret.module.css';
import OCButton from '../../OCButton/index';
import { getCountriesWithSubdivisions } from '../utils/getCountriesWithSubdivisions';

import styles from './styles/DesktopDropdown.module.css';

interface DesktopStateDropdownProps {
  firstLevelNav: NavItemNoIcon[];
  selectedState: string;
  setSelectedState: (state: string) => void;
  stateOptions: Array<{ code: string; label: string }>;
}

const DesktopStateDropdown: React.FC<DesktopStateDropdownProps> = ({
  firstLevelNav,
  selectedState,
  setSelectedState,
  stateOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Unique per instance so aria-controls/id stays valid if more than one dropdown renders on a page.
  const panelId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const countries = getCountriesWithSubdivisions(firstLevelNav);

  if (countries?.length === 0) {
    return null;
  }

  const supportedStateSet = new Set(stateOptions.map(state => state.code));

  const selectState = (code: string) => {
    setSelectedState(code);
    setIsOpen(false);
  };

  return (
    <div className={styles.stateDropdown} data-open={isOpen} data-testid="desktop-states" ref={containerRef}>
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={styles.cta}
        data-testid="state-dropdown-trigger"
        onClick={() => setIsOpen(prev => !prev)}
        type="button"
      >
        {selectedState}
        <span aria-hidden="true" className={classnames(caretStyles.caret, isOpen && caretStyles.caretOpen)} />
      </button>
      <div className={styles.panelWrapper}>
        <div className={styles.states} id={panelId}>
          <div className={styles.statesScroll}>
            {countries.map(country => {
              const subdivisions = Object.entries(country.subdivisions)
                .filter(([code]) => supportedStateSet.has(code))
                .map(([code, details]) => ({
                  code,
                  name: details.name,
                }));

              if (!subdivisions.length) {
                return null;
              }

              const tableCount = country.name === 'United States' ? 10 : 5;
              const columnCount = Math.ceil(subdivisions.length / tableCount);
              const rowCount = Math.min(subdivisions.length, tableCount);

              return (
                <React.Fragment key={country.name}>
                  <div className={styles.countryTitle} data-testid="country-title">
                    {country.name}
                  </div>
                  <div
                    className={styles.stateGrid}
                    data-testid="state-grid"
                    style={{
                      gridTemplateColumns: `repeat(${columnCount}, ${435 / columnCount}px)`,
                      gridTemplateRows: `repeat(${rowCount}, 1fr)`,
                    }}
                  >
                    {subdivisions.map(subdivision => {
                      return (
                        <OCButton
                          className={classnames(
                            styles.state,
                            subdivision.code === selectedState ? styles.selected : '',
                          )}
                          key={subdivision.code}
                          onClick={() => selectState(subdivision.code)}
                        >
                          {subdivision.name}
                          <div className={styles.checkmark}>
                            <Icon height={16} icon="CircleTick" width={16} />
                          </div>
                        </OCButton>
                      );
                    })}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopStateDropdown;
